<template>
	<Drawer
		:title="title ?? $t('Select goals')"
		initialWidth="3xl"
		:closeOnClickOutside="false"
		@requestUnmount="$emit('requestUnmount')"
	>
		<template #header>
			<div class="flex items-start justify-between w-full">
				<div class="font-semibold text-md">{{ title ?? $t("Select goals") }}</div>
			</div>
		</template>
		<template #default="{ hide }">
			<div class="pt-4">
				<div class="pt-2">
					<div v-if="!goalLibraryLoaded" class="text-center py-4">
						<span class="loading loading-spinner loading-sm"></span>
					</div>

					<div v-else class="drawer-content pb-4">
						<GoalFilter
							:filter="filter"
							:initialFilter="goalsStore.getInitialFilter()"
							:goalSources="goalSources"
							:collapsable="false"
							:collapsed="false"
							:toggleButtonLabel="''"
							class="mb-8 relative z-10"
							@change="applyFilter"
							:localStorageKey="'goal-selector-' + curriculum.id + '-filter'"
						/>

						<GoalList
							:store="goalsStore"
							:action="goalListAction"
							:checkedFunction="isChecked"
							:changedFunction="isChanged"
							@itemChecked="addGoal"
							@itemUnchecked="removeGoal"
							class="relative z-0"
						/>
					</div>
				</div>
			</div>

			<div class="drawer-action-bar justify-end gap-2 text-base-content-light">
				<div v-if="changedCount > 0" class="flex items-center gap-2 self-center mr-4">
					<div :class="[counterPulsate ? 'counter-pulsate' : '']">
						{{ changedCount }}
					</div>
					<span class="lowercase">{{ $tc("Changes", changedCount) }}</span>
				</div>
				<button type="button" class="btn" @click="cancelAndClose(hide)">
					{{ $t("Cancel") }}
				</button>
				<button type="button" class="btn btn-primary" @click="saveAndClose(hide)">
					{{ $t("Save") }}
				</button>
			</div>
		</template>
	</Drawer>
</template>

<style>
/* Counter-only pulsate: size change only (no opacity fade) */
@keyframes counter-pulsate {
	0% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.25);
	}
	100% {
		transform: scale(1);
	}
}

.counter-pulsate {
	animation: counter-pulsate 0.6s ease 1;
}
</style>

<script lang="ts">
import { mapStores } from "pinia";
import { useClassroomsStore } from "@/stores/Classrooms.store.ts";

import { Curriculum } from "@/models/Curriculum.model.ts";
import { Goal } from "@/models/Goal.model.ts";
import Drawer from "@/components/ui/Drawer.v2.vue";
import GoalList from "@/components/GoalList.vue";
import GoalFilter from "@/components/GoalFilter.vue";
import { GoalSource } from "@/utils/GoalSource.ts";
import { GOAL_SCOPES, GoalFilterDef } from "@/stores/filters/GoalFilterDef.ts";
import { useOrganisationsStore } from "@/stores/Organisations.store.ts";
import { useGoalsStore } from "@/stores/Goals.store.ts";
import { FixedGoalSource } from "@/goals/utils/GoalPickerLaunch.util";

export default {
	emits: ["requestUnmount", "goalSelected", "goalUnselected"],

	props: {
		curriculum: {
			type: Curriculum,
			required: true,
		},

		selectedGoals: {
			type: Array,
			default: () => [],
		},

		title: {
			type: String,
			default: undefined,
		},

		// The launcher resolves the source before the drawer opens (spec 3.2):
		// the drawer opens directly on the curriculum tree or the prefiltered
		// collection — there is no source-choice step.
		fixedSource: {
			type: Object as () => FixedGoalSource,
			required: true,
		},
	},

	components: {
		GoalFilter,
		GoalList,
		Drawer,
	},

	data() {
		return {
			// Local buffer for selections so changes are only emitted on Save
			localSelectedGoals: [] as any[],
			counterPulsate: false,
			showAllCurriculumGoals: false,
			selectedGoalCollectionId: null as string | null,
			selectedGoalScope: null as GOAL_SCOPES | null,

			goalLibraryLoaded: false,
			goalSources: [] as GoalSource[],
			filter: new GoalFilterDef(),
		};
	},

	async mounted() {
		// Initialize local buffer from incoming prop so selections are only finalized on Save
		this.localSelectedGoals = Array.isArray(this.selectedGoals) ? [...this.selectedGoals] : [];

		// The goals store is shared; scope it to this selector's single curriculum so
		// display, filter options and source supplementing never leak goals from another
		// curriculum that a previous (multi-curriculum) view may have loaded.
		this.goalsStore.setCurriculum(this.curriculum);

		if ("collection" in this.fixedSource) {
			await this.selectGoalCollection(
				this.fixedSource.collection.id,
				this.fixedSource.notYetCovered ? GOAL_SCOPES.UNPLANNED : null,
			);
		} else {
			this.showAllCurriculumGoals = true;
			await this.load();
		}
	},

	computed: {
		...mapStores(useOrganisationsStore, useClassroomsStore, useGoalsStore),

		/**
		 * Number of changed items compared to the last saved selection (added + removed)
		 */
		changedCount() {
			const original = Array.isArray(this.selectedGoals) ? this.selectedGoals : [];
			const added = this.localSelectedGoals.filter(
				(l) => !original.some((o) => this.goalsStore.representsSameModel(o, l))
			);
			const removed = original.filter(
				(o) => !this.localSelectedGoals.some((l) => this.goalsStore.representsSameModel(l, o))
			);
			return added.length + removed.length + this.mutedChangedGoals.length;
		},

		/** Buffered goals whose muted flag differs from the original selection (subgoal (un)ticks). */
		mutedChangedGoals() {
			const original = Array.isArray(this.selectedGoals) ? this.selectedGoals : [];
			return this.localSelectedGoals.filter((l) => {
				const o = original.find((o) => this.goalsStore.representsSameModel(o, l));
				return o && (o.muted ?? false) !== (l.muted ?? false);
			});
		},

		goalListAction() {
			if (this.showAllCurriculumGoals) {
				return "import";
			} else {
				return "select";
			}
		},
	},

	watch: {
		changedCount(newVal, oldVal) {
			if (newVal !== oldVal) this.pulseCounter();
		},
	},

	methods: {
		addGoal(goal: Goal) {
			// Should we set the collection id for newly selected items in the buffer?
			if (!this.showAllCurriculumGoals && this.selectedGoalCollectionId) {
				goal.goalCollectionId = this.selectedGoalCollectionId;
			}

			// Ticking a subgoal includes it (unmuted). Its parent — and with it the other
			// subgoals — comes along when it is not buffered yet.
			if (goal.isSubgoal()) {
				const parentGoal = this.goalsStore.findParentGoal(goal);
				if (
					parentGoal &&
					!this.localSelectedGoals.some((g) => this.goalsStore.representsSameModel(g, parentGoal))
				) {
					this.addGoal(parentGoal);
				}
				const index = this.localSelectedGoals.findIndex((g) => this.goalsStore.representsSameModel(g, goal));
				if (index === -1) {
					this.localSelectedGoals.push(goal);
				} else if (this.localSelectedGoals[index].muted) {
					// Clone-and-replace: the buffered instance may be shared with the caller's model.
					const updated = this.localSelectedGoals[index].clone();
					updated.muted = false;
					this.localSelectedGoals.splice(index, 1, updated);
				}
				this.pulseCounter();
				return;
			}

			// Add to local buffer if not already present
			if (!this.localSelectedGoals.some((g) => this.goalsStore.representsSameModel(g, goal))) {
				this.localSelectedGoals.push(goal);
				this.pulseCounter();
			}

			// A parent brings every loaded subgoal along (GO! MIA/THB elements are one unit
			// with their goal); unticking individual ones afterwards mutes them instead.
			for (const subgoal of this.subgoalsOf(goal)) {
				if (!this.localSelectedGoals.some((g) => this.goalsStore.representsSameModel(g, subgoal))) {
					if (!this.showAllCurriculumGoals && this.selectedGoalCollectionId) {
						subgoal.goalCollectionId = this.selectedGoalCollectionId;
					}
					this.localSelectedGoals.push(subgoal);
				}
			}
		},

		removeGoal(goal: Goal) {
			// Unticking a subgoal never removes it: it stays with its parent, muted.
			if (goal.isSubgoal()) {
				const index = this.localSelectedGoals.findIndex((g) => this.goalsStore.representsSameModel(g, goal));
				if (index === -1) {
					return;
				}
				const parentBuffered = this.localSelectedGoals.some(
					(g) => !g.isSubgoal()
						&& g.source === goal.source
						&& g.fullPath === goal.sourceParentPath
				);
				if (parentBuffered) {
					const updated = this.localSelectedGoals[index].clone();
					updated.muted = true;
					this.localSelectedGoals.splice(index, 1, updated);
				} else {
					// No parent to stay with (stale selection): drop it entirely.
					this.localSelectedGoals.splice(index, 1);
				}
				this.pulseCounter();
				return;
			}

			this.localSelectedGoals = this.localSelectedGoals.filter(
				(g) => !this.goalsStore.representsSameModel(g, goal)
					&& !(g.isSubgoal() && g.source === goal.source && g.sourceParentPath === goal.fullPath)
			);
			this.pulseCounter();
		},

		/** Loaded subgoals of a parent goal (same source, parent path equal to its full path). */
		subgoalsOf(goal: Goal): Goal[] {
			if (goal.isSubgoal()) {
				return [];
			}
			return (this.goalsStore.items as Goal[]).filter(
				(item) => item.isSubgoal()
					&& item.source === goal.source
					&& item.sourceParentPath === goal.fullPath
			);
		},

		pulseCounter() {
			// Trigger pulse animation on the counter for the duration of the pulsate keyframes
			this.counterPulsate = false;
			// next tick ensure class toggles
			setTimeout(() => {
				this.counterPulsate = true;
				setTimeout(() => (this.counterPulsate = false), 600);
			}, 0);
		},

		async selectGoalCollection(goalCollectionId: string, goalScope: GOAL_SCOPES | null = null) {
			this.showAllCurriculumGoals = false;
			this.selectedGoalCollectionId = goalCollectionId;
			this.selectedGoalScope = goalScope;

			await this.load();
		},

		async load() {
			if (!this.curriculum) {
				throw new Error("No curriculum set");
			}

			this.goalLibraryLoaded = false;

			// We want to load the goals that were not planned yet.
			if (this.selectedGoalScope && this.selectedGoalCollectionId) {
				const classroom = this.classroomsStore.getCurrentClassroom();
				const organisation = this.organisationsStore.getCurrentOrganisation();
				const schoolYearInterval = organisation.getCurrentSchoolYearInterval();

				this.filter.goalScopes = [this.selectedGoalScope];
				this.filter.collection = this.selectedGoalCollectionId;
				this.filter.classroomIds = [classroom.id];
				this.filter.start = schoolYearInterval.start.toJSDate();
				this.filter.end = schoolYearInterval.end.toJSDate();
			} else {
				this.filter.goalScopes = [];
				this.filter.collection = null;
				this.filter.classroomIds = [];
			}

			this.goalsStore.collectionId = this.selectedGoalCollectionId;
			await this.goalsStore.applyFilter(this.filter);

			this.goalSources = this.goalsStore.allSources;

			this.goalLibraryLoaded = true;

			// If showAllCurriculumGoals is true, we want to supplement the goals store with goals from the source
			// This is useful when the user wants to see goals that are not used in the currently selected goalCollection
			if (this.showAllCurriculumGoals) {
				this.goalsStore.supplementWithGoalsFromSource();
			} else {
				this.goalsStore.removeNonPersistedItems();
			}
		},

		async applyFilter(filterDef: GoalFilterDef) {
			this.filter = filterDef;
			await this.goalsStore.applyFilter(filterDef);
		},

		isChecked(goal: Goal) {
			const buffered = this.localSelectedGoals.find((g) => this.goalsStore.representsSameModel(g, goal));
			if (!buffered) {
				return false;
			}
			// A subgoal's box shows whether it is included (unmuted), not merely buffered.
			return goal.isSubgoal() ? !buffered.muted : true;
		},

		isChanged(goal: Goal) {
			const original = Array.isArray(this.selectedGoals) ? this.selectedGoals : [];
			const inOriginal = original.some((o) => this.goalsStore.representsSameModel(o, goal));
			const inLocal = this.localSelectedGoals.some((l) => this.goalsStore.representsSameModel(l, goal));
			if (inLocal && !inOriginal) return "added";
			if (!inLocal && inOriginal) return "removed";
			return false;
		},

		/**
		 * Save current local selections: emit added/removed events compared to original prop,
		 * then close the drawer using the provided hide() function from the Drawer slot.
		 */
		saveAndClose(hide: () => void) {
			const original = Array.isArray(this.selectedGoals) ? this.selectedGoals : [];
			const added = this.localSelectedGoals.filter(
				(l) => !original.some((o) => this.goalsStore.representsSameModel(o, l))
			);
			const removed = original.filter(
				(o) => !this.localSelectedGoals.some((l) => this.goalsStore.representsSameModel(l, o))
			);

			added.forEach((g) => this.$emit("goalSelected", g));
			removed.forEach((g) => this.$emit("goalUnselected", g));
			// Muted flips on goals that were already selected: re-emit, the form syncs muted.
			this.mutedChangedGoals.forEach((g) => this.$emit("goalSelected", g));

			hide();
		},

		cancelAndClose(hide: () => void) {
			// revert buffer to original selection and close without emitting
			this.localSelectedGoals = Array.isArray(this.selectedGoals) ? [...this.selectedGoals] : [];
			hide();
		},
	},
};
</script>
